import { MappingSchemaData, MappingSchemaSingleRow, SearchResponse } from "interfaces/dataInterfaces"
import { blobToDownload } from "utils/helpers"
import { marketpricesApi } from "./marketpricesApi"

export const mappingSchemaApi = marketpricesApi.injectEndpoints({
  endpoints: builder => ({
    getMS: builder.query<
      SearchResponse<MappingSchemaData>,
      { adm0code?: number; query?: string; skip: number; top: number; statusId?: number }
    >({
      query: ({ adm0code, skip, top, query, statusId }) => ({
        url: "MappingSchemas/Get",
        method: "GET",
        params: { adm0code, skip, top, search: query, statusId },
      }),
      providesTags: ["MappingSchema"],
    }),

    deleteMS: builder.mutation<void, number>({
      query: id => ({ url: "MappingSchemas/Delete", method: "DELETE", params: { id } }),
      invalidatesTags: ["MappingSchema"],
    }),

    saveMSDetails: builder.mutation<
      { id: number },
      { adm0code: number; mappingSchemaName: string; mappingSchemaTypeId: number | undefined; xlsFormId?: number }
    >({
      query: body => ({
        url: "MappingSchemas/SaveDetails",
        method: "POST",
        body: { ...body, mappingSchemaStatusID: 1 },
      }),
      invalidatesTags: ["MappingSchema"],
    }),

    saveMSExcel: builder.mutation<{ fileName: string }, FormData>({
      query: body => ({ url: "MappingSchemas/SaveExcelFileForMapping", method: "POST", body }),
    }),

    getMSExcelAsJson: builder.query<Record<string, string>[], { fileName?: string; msId?: number }>({
      query: ({ fileName, msId }) => ({
        url: "MappingSchemas/GetExcelAsJson",
        method: "GET",
        params: { fileName, msId },
      }),
    }),

    getMSById: builder.query<MappingSchemaData, number>({
      query: id => ({ url: "MappingSchemas/GetById", method: "GET", params: { id } }),
      keepUnusedDataFor: 1,
    }),

    updateMSJsonSchema: builder.mutation<string, { msId: number; schema: string }>({
      query: ({ msId, schema }) => ({
        url: "MappingSchemas/UpdateJsonSchema",
        method: "PUT",
        body: { msId, schema },
      }),
      invalidatesTags: ["MappingSchema"],
    }),

    getMSSingleRow: builder.query<MappingSchemaSingleRow, { rowHeaderPosition: number; fileName: string }>({
      query: ({ rowHeaderPosition, fileName }) => ({
        url: "MappingSchemas/GetSingleRow",
        method: "GET",
        params: { rowHeaderPosition, fileName },
      }),
    }),

    downloadMappingExcelFile: builder.mutation<void, { msId: string; fileName?: string }>({
      query: ({ msId, fileName }) => ({
        url: "MappingSchemas/DownloadOriginMappingSchema",
        method: "GET",
        params: { mpId: msId },
        responseHandler: async (response: Response) => {
          if (response.ok) await blobToDownload(response, `${fileName ?? "mapping-schema"}.xlsx`)
        },
      }),
    }),

    downloadExcelTemplate: builder.mutation<void, { adm0Code: number; reportId: number }>({
      query: ({ adm0Code, reportId }) => ({
        url: "MappingSchemas/DownloadExcelTemplate",
        method: "GET",
        params: { adm0Code, reportId },
      }),
    }),
  }),
})

export const {
  useGetMSQuery,
  useSaveMSDetailsMutation,
  useSaveMSExcelMutation,
  useGetMSExcelAsJsonQuery,
  useLazyGetMSExcelAsJsonQuery,
  useGetMSByIdQuery,
  useLazyGetMSByIdQuery,
  useUpdateMSJsonSchemaMutation,
  useGetMSSingleRowQuery,
  useDeleteMSMutation,
  useDownloadMappingExcelFileMutation,
  useDownloadExcelTemplateMutation,
} = mappingSchemaApi
